import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { trackAddToSelection } from '../lib/analytics';

const i18n = {
  skip: { es: 'Sin cambios', en: 'No changes', cat: 'Sense canvis', fr: 'Sans changement' },
  done: { es: '¡Listo!', en: 'Done!', cat: 'Llest!', fr: 'Terminé !' },
  next: { es: 'Siguiente', en: 'Next', cat: 'Següent', fr: 'Suivant' },
  add: { es: 'Añadir', en: 'Add', cat: 'Afegir', fr: 'Ajouter' },
  addAsIs: { es: 'Añadir tal cual', en: 'Add as is', cat: 'Afegir tal qual', fr: 'Ajouter tel quel' },
  customize: { es: '¿Quieres quitar algo?', en: 'Remove anything?', cat: 'Vols treure alguna cosa?', fr: 'Retirer quelque chose ?' },
  extras: { es: '¿Algún extra?', en: 'Any extras?', cat: 'Algun extra?', fr: 'Des extras ?' },
  supplements: { es: '¿Algo más?', en: 'Anything else?', cat: 'Alguna cosa més?', fr: 'Autre chose ?' },
  howPrefer: { es: '¿Cómo lo prefieres?', en: 'How would you like it?', cat: 'Com el prefereixes?', fr: 'Comment le préférez-vous ?' },
  standardServing: { es: 'Copa con hielo', en: 'Glass with ice', cat: 'Copa amb gel', fr: 'Verre avec glace' },
  groupTitles: {
    tipo_pan: { es: '¿Qué pan prefieres?', en: 'Which bread?', cat: 'Quin pa prefereixes?', fr: 'Quel pain ?' },
    tipo_vino: { es: '¿Qué tipo de vino?', en: 'Which wine?', cat: 'Quin tipus de vi?', fr: 'Quel vin ?' },
    ensalada: { es: '¿Qué ensalada?', en: 'Which salad?', cat: 'Quina amanida?', fr: 'Quelle salade ?' },
    principal: { es: '¿Qué plato principal?', en: 'Which main dish?', cat: 'Quin plat principal?', fr: 'Quel plat ?' },
    postre: { es: '¿Qué postre?', en: 'Which dessert?', cat: 'Quines postres?', fr: 'Quel dessert ?' },
    tipo_bebida: { es: '¿Qué bebida prefieres?', en: 'Which drink?', cat: 'Quina beguda prefereixes?', fr: 'Quelle boisson ?' },
    tipo_leche: { es: '¿Qué tipo de leche?', en: 'Milk type?', cat: 'Quin tipus de llet?', fr: 'Type de lait ?' },
    tipo_bikini: { es: '¿Qué bikini prefieres?', en: 'Which bikini?', cat: 'Quin bikini prefereixes?', fr: 'Quel bikini ?' },
  },
};

export const useWizardSteps = (product, language, localize) => {
  const t = useCallback((key) => i18n[key]?.[language] || i18n[key]?.es || key, [language]);

  const steps = useMemo(() => {
    if (!product) return [];
    const result = [];
    const options = product.options || [];
    const supplements = product.supplements || [];

    // Group options by group
    const groups = {};
    const ungrouped = [];
    options.forEach(opt => {
      if (opt.group) {
        if (!groups[opt.group]) groups[opt.group] = [];
        groups[opt.group].push(opt);
      } else {
        ungrouped.push(opt);
      }
    });

    // One step per group
    Object.entries(groups).forEach(([groupName, groupOpts]) => {
      const isRequired = product.requires_selection ||
        (groupName === 'tipo_pan' && product.requires_bread_selection) ||
        (groupName === 'tipo_vino' && product.requires_option_selection);
      result.push({
        type: 'group', groupName,
        title: i18n.groupTitles[groupName]?.[language] || i18n.groupTitles[groupName]?.es || groupName,
        options: groupOpts, required: isRequired, multiSelect: false,
      });
    });

    // Classify ungrouped options
    const hasGroups = Object.keys(groups).length > 0;
    const removalPattern = /^(sin |sense |without |sans |no |solo )/i;
    let removals = ungrouped.filter(o => removalPattern.test(localize(o.name)));
    const choices = ungrouped.filter(o => !removalPattern.test(localize(o.name)));
    const hasPriceVariants = choices.length > 0 && choices.some(o => o.price_modifier && o.price_modifier !== 0);

    // Filter "Solo pan (sin nada)" when individual removals exist
    const soloPattern = /^solo /i;
    const individualRemovals = removals.filter(o => !soloPattern.test(localize(o.name)));
    if (individualRemovals.length > 0 && individualRemovals.length < removals.length) {
      removals = individualRemovals;
    }

    // Binary CHOICE pattern
    if (removals.length > 0 && removals.length <= 2 && choices.length === 0 && !hasGroups) {
      const basePrice = product.price || 0;
      const baseLabel = language === 'es' ? 'Normal' : language === 'en' ? 'Regular' : language === 'cat' ? 'Normal' : 'Normal';
      const allOpts = [
        { name: { es: baseLabel, en: 'Regular', cat: 'Normal', fr: 'Normal' }, price_modifier: 0, _absolutePrice: basePrice, _isBase: true, _isChoice: true },
        ...removals.map(o => ({ ...o, _absolutePrice: basePrice + (o.price_modifier || 0), _isChoice: true })),
      ];
      result.push({ type: 'choice', groupName: null, title: t('howPrefer'), options: allOpts, required: false, multiSelect: false, _showPrices: removals.some(o => o.price_modifier && o.price_modifier !== 0) });
    } else {
      if (choices.length > 0 && !hasPriceVariants) {
        result.push({ type: 'choice', groupName: null, title: t('howPrefer'), options: choices, required: false, multiSelect: false });
      }
      if (choices.length > 0 && hasPriceVariants) {
        const basePrice = product.price || 0;
        const variantOpts = choices.map(o => ({ ...o, _absolutePrice: basePrice + (o.price_modifier || 0), _isVariant: true }));
        const baseOpt = { name: { es: t('standardServing'), en: t('standardServing'), cat: t('standardServing'), fr: t('standardServing') }, price_modifier: 0, _absolutePrice: basePrice, _isBase: true, _isVariant: true };
        result.push({ type: 'variant', groupName: null, title: t('howPrefer'), options: [baseOpt, ...variantOpts], required: false, multiSelect: false });
      }
      if (removals.length > 0) {
        const allZeroMods = removals.every(o => !o.price_modifier || o.price_modifier === 0);
        result.push({ type: 'extras', groupName: null, title: t('customize'), options: removals, required: false, multiSelect: true, _allZeroMods: allZeroMods });
      }
    }

    if (supplements.length > 0) {
      result.push({ type: 'supplements', groupName: null, title: t('supplements'), options: supplements, required: false, multiSelect: true });
    }

    return result;
  }, [product, language, t, localize]);

  return { steps, t };
};

export const useWizardNavigation = (steps, product, addItem, onComplete) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [slideDir, setSlideDir] = useState('in');

  const selectedOptionsRef = useRef(selectedOptions);
  useEffect(() => { selectedOptionsRef.current = selectedOptions; }, [selectedOptions]);

  const totalSteps = steps.length;
  const step = steps[currentStep];

  const shouldSkipStep = useCallback((stepIndex) => {
    const targetStep = steps[stepIndex];
    if (!targetStep) return false;
    if (targetStep.groupName === 'tipo_leche') {
      const currentOpts = selectedOptionsRef.current;
      const selectedBeverage = currentOpts.find(o => o.group === 'tipo_bebida');
      if (selectedBeverage?.no_milk) return true;
    }
    return false;
  }, [steps]);

  const findNextStep = useCallback((fromStep) => {
    let next = fromStep + 1;
    while (next < steps.length && shouldSkipStep(next)) next++;
    return next;
  }, [steps, shouldSkipStep]);

  const isLastStep = findNextStep(currentStep) >= totalSteps;

  const completeWizard = useCallback(() => {
    if (isCompleting) return;
    setIsCompleting(true);
    trackAddToSelection(product, 1, selectedOptionsRef.current);
    addItem(product, 1, selectedOptionsRef.current);
    setTimeout(() => onComplete(product, selectedOptionsRef.current), 600);
  }, [product, addItem, onComplete, isCompleting]);

  const advanceStep = useCallback(() => {
    const nextStep = findNextStep(currentStep);
    const skippedGroups = [];
    for (let i = currentStep + 1; i < nextStep; i++) {
      if (steps[i]?.groupName) skippedGroups.push(steps[i].groupName);
    }
    if (skippedGroups.length > 0) {
      setSelectedOptions(prev => prev.filter(o => !skippedGroups.includes(o.group)));
    }
    if (nextStep >= steps.length) {
      completeWizard();
    } else {
      setSlideDir('out');
      setTimeout(() => { setCurrentStep(nextStep); setSlideDir('in'); }, 50);
    }
  }, [currentStep, steps, findNextStep, completeWizard]);

  const totalPrice = useMemo(() => {
    const optionsExtra = selectedOptions.reduce((acc, opt) => acc + (opt.price_modifier || 0), 0);
    return (product?.price || 0) + optionsExtra;
  }, [product, selectedOptions]);

  return {
    currentStep, step, totalSteps, slideDir, isLastStep,
    selectedOptions, setSelectedOptions, isCompleting,
    advanceStep, completeWizard, totalPrice
  };
};
